'use client';

import { useRef, useState } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { ModalDialog, ModalDialogProps } from '..';
import { BodyMedium } from '../../styles';
import { CropImage, CropModalContent } from './styles';
import { centerAspectCrop, makeCanvasPreview } from './cropping-utils';
import { useBreakpoint, useDebounceEffect } from '../../hooks';
import { isCompact } from '../../types';
import { useTranslations } from 'next-intl';

export enum Aspect {
    Landscape = 16 / 9,
    Square = 1
}

interface Props extends Omit<ModalDialogProps, 'children' | 'onSubmit'> {
    imageSrc: string;
    mimeType?: string;
    aspect?: Aspect | number;
    onSave: (croppedImage: Blob) => void;
    onError: () => void;
    circular?: boolean;
}

const CropModal = ({
    onError,
    title,
    imageSrc,
    mimeType,
    onSave,
    aspect,
    circular = false,
    ...props
}: Props) => {
    const t_dialog = useTranslations('dialog');
    const t = useTranslations('file_upload');
    const breakpoint = useBreakpoint();

    const previewCanvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [limiter, setLimiter] = useState<'height' | 'width'>();

    useDebounceEffect(
        async () => {
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            )
                // draw the cropped selection
                makeCanvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
        },
        500,
        [completedCrop]
    );

    const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = e.currentTarget;
        const newCrop = centerAspectCrop(width, height, aspect);

        const isPortrait = width < height;
        const isDesktop = window.innerWidth > window.innerHeight;
        const windowRatio = window.innerHeight / window.innerWidth;
        const imageRatio = height / width;
        if (isPortrait && isDesktop) setLimiter('height');
        if (!isPortrait && !isDesktop) setLimiter('width');
        else windowRatio < imageRatio ? setLimiter('height') : setLimiter('width');

        newCrop !== crop && setCrop(newCrop);
    };

    // function stolen from https://codesandbox.io/p/sandbox/react-image-crop-demo-with-react-hooks-y831o
    const onSaveCrop = async () => {
        const image = imgRef.current;
        const previewCanvas = previewCanvasRef.current;
        if (!image || !previewCanvas || !completedCrop) {
            throw new Error('Crop canvas does not exist');
        }

        // This will size relative to the uploaded image
        // size. If you want to size according to what they
        // are looking at on screen, remove scaleX + scaleY
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const offscreen = new OffscreenCanvas(
            completedCrop.width * scaleX,
            completedCrop.height * scaleY
        );

        const ctx = offscreen.getContext('2d');
        if (!ctx) throw new Error('No 2d context');

        // Draw the crop in our offscreen canvas
        (ctx as OffscreenCanvasRenderingContext2D).drawImage(
            previewCanvas,
            0,
            0,
            previewCanvas.width,
            previewCanvas.height,
            0,
            0,
            offscreen.width,
            offscreen.height
        );

        const blob = await offscreen.convertToBlob({
            type: mimeType ?? 'image/jpeg'
            // quality: 0.8
        });

        onSave(blob);
    };

    return (
        <ModalDialog
            {...props}
            title={title}
            submitText={props.submitText ?? t_dialog('save')}
            onSubmit={onSaveCrop}
            modalContentStyle={{ maxHeight: '85%' }}
        >
            <CropModalContent>
                {isCompact(breakpoint) ? null : <BodyMedium>{t('image_crop_text')}</BodyMedium>}
                <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={aspect}
                    minHeight={100}
                    circularCrop={circular}
                >
                    <CropImage
                        alt={title ?? 'title image'}
                        ref={imgRef}
                        width={0}
                        height={0}
                        src={imageSrc}
                        onLoad={onImageLoad}
                        onError={onError}
                        limiter={limiter}
                    />
                </ReactCrop>
                <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
            </CropModalContent>
        </ModalDialog>
    );
};

export { CropModal };
